import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import MD5 from 'crypto-js/md5';
import { gridAPI } from 'api/grid';
import { isEqual } from 'lodash';
import { applyGridSettings } from './useGridSettings';
import { applyGridData } from './useGridDataSync';
import { FilterValue, RelatesFields, VisibleColumns, GridState } from 'components/common/grid/';

type GridAction = Partial<GridState>;

const initialState: GridState = {
    filtersValue: {},
    filtersList: [],
    pageIndex: 0,
    order: 'asc',
    orderBy: '',
    gridData: [],
    columns: [],
    totalCount: 0,
    gridName: '',
    settings: null,
    resetFiltersButton: false,
    searchField: {},
    legendConfig: [],
    rowExtraInfoConfig: undefined,
    pagerOptions: { pageSize: 0, visible: false },
    gridTitle: '',
    gridEntityType: '',
    titleHidden: false,
    gridDescription: null,
    dragAndDrop: { enabled: false, url: '' },
    massActions: { enabled: false },
    buttons: [],
    controllerUrl: '',
    emptyTitle: 'empty_grid_title.no_data_found',
    emptyIcon: '',
    hideEmpty: false,
    resetColumns: true,
    switchColumns: true,
    widthToggle: false,
    saveFiltersValues: false,
    dependentGrids: [],
    noSearchResult: undefined,
    infoButton: undefined,
    visibleFilters: [],
    multilines: false,
    autoRowHeight: false,
    defaultRowHeight: 32,
    gridParams: null,
    changeViewConfig: null,
    gridReorderLoading: false,
    customLegend: false,
    dataLoadingDone: false,
};

function reducer(state: GridState, action: GridAction): GridState {
    return { ...state, ...action };
}

export const useFetchGridData = (uid: string, settingsUrl: string, relatedFields: RelatesFields = {}) => {
    const queryClient = useQueryClient();
    const [state, dispatch] = useReducer(reducer, initialState);

    const requestParamsHashRef = useRef<string>('');

    const requestParams = useMemo(
        () => ({
            page: state.pageIndex,
            order: state.orderBy,
            order_type: state.order,
            filters: state.filtersValue,
            related_fields: relatedFields,
        }),
        [state.pageIndex, state.orderBy, state.order, state.filtersValue, relatedFields],
    );

    const requestParamsHash = useMemo(() => {
        const newHash = MD5(JSON.stringify(requestParams)).toString();
        requestParamsHashRef.current = newHash;
        return newHash;
    }, [requestParams]);

    const queryKey = useMemo(() => [uid, settingsUrl, requestParamsHash], [uid, settingsUrl, requestParamsHash]);

    const { data, isLoading, refetch, isError, error } = useQuery<any, Error>({
        queryKey,
        queryFn: () => gridAPI.load(settingsUrl, requestParams),
        gcTime: 0,
        enabled: !!settingsUrl,
    });

    useEffect(() => {
        if (data?.data?.settings) {
            applyGridSettings(settingsUrl, data.data.settings, dispatch, state.filtersValue);
        }

        if (data?.data?.data) {
            if (data.data.data.rows.length === 0 && state.pageIndex > 0) {
                setPageIndex(state.pageIndex - 1);
            } else {
                applyGridData(data.data.data, dispatch);
            }
        }
    }, [data]);

    useEffect(() => {
        if (state.filtersList.length > 0 && Object.keys(state.filtersValue).length === 0) {
            const defaultFilters: FilterValue = {};
            state.filtersList.forEach((group: any[]) => {
                group.forEach(({ name, defaultValue }) => {
                    if (defaultValue !== undefined && defaultFilters[name] === undefined) {
                        defaultFilters[name] = defaultValue;
                    }
                });
            });
            if (Object.keys(defaultFilters).length > 0) {
                dispatch({ filtersValue: defaultFilters });
            }
        }
    }, [state.filtersList]);

    const setPageIndex = useCallback((pageIndex: number) => dispatch({ pageIndex }), []);

    const onSortColumn = useCallback(
        (dataKey: string, sortType: 'asc' | 'desc' = 'asc') => {
            if (dataKey !== state.orderBy) {
                dispatch({ orderBy: dataKey, order: 'asc' });
            } else {
                dispatch({ order: sortType });
            }
            gridAPI.saveGridOrder({ gridName: state.gridName, order: { order: sortType, orderBy: dataKey } });
        },
        [state.orderBy, state.gridName],
    );

    const handleFilterValueChange = useCallback(
        (filterName: string, value: any) => {
            const newFiltersValue = { ...state.filtersValue, [filterName]: value };
            if (!isEqual(newFiltersValue, state.filtersValue)) {
                dispatch({ filtersValue: newFiltersValue, pageIndex: 0 });
                if (state.saveFiltersValues) {
                    gridAPI.saveGridFiltersValues({ gridName: state.gridName, filtersValues: newFiltersValue });
                }
            }
        },
        [state.filtersValue, state.saveFiltersValues, state.gridName],
    );

    const removeFilterValue = useCallback(
        (filterName: string) => {
            const newFiltersValue = { ...state.filtersValue };
            delete newFiltersValue[filterName];
            dispatch({ filtersValue: newFiltersValue, pageIndex: 0 });
            if (state.saveFiltersValues) {
                gridAPI.saveGridFiltersValues({ gridName: state.gridName, filtersValues: newFiltersValue });
            }
        },
        [state.filtersValue, state.saveFiltersValues, state.gridName],
    );

    const resetFilters = useCallback(() => {
        dispatch({ filtersValue: {}, pageIndex: 0 });
        if (state.saveFiltersValues) {
            gridAPI.saveGridFiltersValues({ gridName: state.gridName, filtersValues: {} });
        }
    }, [state.gridName, state.saveFiltersValues]);

    const reloadGridData = useCallback(async () => {
        if (state.dependentGrids.length > 0) {
            state.dependentGrids.forEach((gridName) => {
                queryClient.invalidateQueries({ queryKey: [gridName] });
            });
        }
        await refetch();
    }, [state.dependentGrids, queryClient, refetch]);

    const gridDataMap = useMemo(() => {
        const map = new Map();
        state.gridData.forEach((item, index) => {
            map.set(item.id, { item, index });
        });
        return map;
    }, [state.gridData]);

    const handleDropRow = useCallback(
        (sourceId: string, targetId: string) => {
            const dragData = gridDataMap.get(sourceId);
            const targetData = gridDataMap.get(targetId);

            if (dragData && targetData) {
                const { item: dragItem, index: dragItemIndex } = dragData;
                const { index: targetItemIndex } = targetData;

                const newGridData = [...state.gridData];
                newGridData.splice(dragItemIndex, 1);
                newGridData.splice(targetItemIndex, 0, dragItem);

                dispatch({ gridReorderLoading: true });

                gridAPI
                    .updateGridOrder(state.dragAndDrop?.url || '', {
                        new_order: newGridData.map((item) => item.id),
                    })
                    .then((response) => {
                        if (response.data.status === 'OK') {
                            refetch();
                        } else {
                            dispatch({ gridReorderLoading: false });
                        }
                    })
                    .catch(() => {
                        dispatch({ gridReorderLoading: false });
                    });
            }
        },
        [gridDataMap, state.gridData, state.dragAndDrop?.url, refetch],
    );

    const saveColumns = useCallback(
        (
            columnsWidth: Record<string, number>,
            visibleColumns: VisibleColumns,
            fullwidthMode: string,
            gridView: string,
            version: number,
        ) => {
            return gridAPI.saveGridSettings({
                gridName: state.gridName,
                colWidth: columnsWidth,
                colVisible: visibleColumns,
                fullwidthMode,
                gridView,
                version,
            });
        },
        [state.gridName],
    );

    return {
        ...state,
        isLoading,
        isError,
        error,
        setPageIndex,
        onSortColumn,
        handleFilterValueChange,
        removeFilterValue,
        resetFilters,
        reloadGridData,
        handleDropRow,
        saveColumns,
    };
};
